import React, { useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { logout } from '~api/services/core/users'
import { broadcastSignOut, onSignOutBroadcast } from '~api/sessionActivity'
import { useSessionExpiry } from '~hooks/useSessionExpiry'
import { type RootState } from '~redux/rootReducer'
import { logoutUser } from '~redux/slices/authSlice'
import { type AppDispatch, persistor } from '~redux/store'

import SessionExpiryBanner from './SessionExpiryBanner'
import SessionExpiryDialog from './SessionExpiryDialog'
import SignedOutOverlay from './SignedOutOverlay'

const SessionTimeoutManager: React.FC = () => {
  const dispatch: AppDispatch = useDispatch()
  const userId = useSelector((state: RootState) => state?.auth.userId)
  const { status, remainingMs } = useSessionExpiry()
  const [warningDismissed, setWarningDismissed] = useState(false)

  // Full local teardown matching the real sign-out: drop all persisted state so
  // nothing stale (userId/sessionId/token) survives, then reload to login.
  const clearLocalSession = useCallback(async (): Promise<void> => {
    await persistor.purge()
    dispatch({ type: 'RESET_STATE' })
    window.location.href = '/'
    window.location.reload()
  }, [dispatch])

  useEffect(() => {
    if (status === 'active') {
      setWarningDismissed(false)
    }
  }, [status])

  useEffect(() => {
    return onSignOutBroadcast(() => {
      void clearLocalSession()
    })
  }, [clearLocalSession])

  const handleSignOutNow = async (): Promise<void> => {
    try {
      if (userId) {
        await logout(userId)
      }
      dispatch(logoutUser())
    } catch (error) {
      console.error('Sign out failed', error)
    } finally {
      broadcastSignOut()
      await clearLocalSession()
    }
  }

  const handleSignIn = async (): Promise<void> => {
    try {
      if (userId) {
        await logout(userId)
      }
    } catch (error) {
      console.error('Sign out failed', error)
    } finally {
      await clearLocalSession()
    }
  }

  if (status === 'expired') {
    return <SignedOutOverlay onSignIn={handleSignIn} />
  }

  if (status === 'warning') {
    if (warningDismissed) {
      return <SessionExpiryBanner remainingMs={remainingMs} onSignOutNow={handleSignOutNow} />
    }
    return (
      <SessionExpiryDialog
        open
        remainingMs={remainingMs}
        onDismiss={() => setWarningDismissed(true)}
        onSignOutNow={handleSignOutNow}
      />
    )
  }

  return null
}

export default SessionTimeoutManager
